import { useState, useEffect, useCallback } from 'react';
import { updateDoc, serverTimestamp } from 'firebase/firestore';
import { propertyDoc } from '../utils/firestorePaths';
import { VENDORS_LIST, VENDOR_NEEDED_ITEM_TYPE } from '../constants/roomConstants';
import { updateMeta } from '../utils/auditFields';

/**
 * Custom hook for room data management
 * @param {Array} items - Array of item objects
 * @param {string} clientId - Owning client ID
 * @param {string} selectedProperty - Selected property ID
 * @param {Array} itemCategories - Array of item category objects with displayInDownRoomsList setting
 * @param {Object} [user] - Acting user (for audit metadata)
 * @returns {Object} - Room data and manipulation functions
 */
export const useRoomData = (items, clientId, selectedProperty, itemCategories = [], user) => {
  const [itemsByCategory, setItemsByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  // Groups items by their type
  const groupItemsByType = useCallback((items) => {
    return items.reduce((acc, item) => {
      if (!acc[item.itemType]) {
        acc[item.itemType] = [];
      }
      acc[item.itemType].push(item);
      return acc;
    }, {});
  }, []);

  // Process items and add vendor category
  useEffect(() => {
    if (!items || items.length === 0) {
      setLoading(false);
      return;
    }

    console.log('useRoomData - itemCategories:', itemCategories);
    console.log('useRoomData - items count:', items.length);

    // Filter items based on their category's displayInDownRoomsList setting
    // For backwards compatibility, if displayInDownRoomsList is not explicitly false, show the item
    const filteredItems = items.filter((item) => {
      // If no categories are loaded yet, don't filter (show all for now)
      if (!itemCategories || itemCategories.length === 0) {
        return true;
      }

      const category = itemCategories.find(
        (cat) => (cat.name || cat.id || cat) === item.itemType
      );

      // If category not found, include the item (backwards compatibility)
      if (!category) {
        console.log(`Category not found for item: ${item.itemName} (type: ${item.itemType})`);
        return true;
      }

      const shouldInclude = category.displayInDownRoomsList !== false;
      if (!shouldInclude) {
        console.log(`Filtering out item: ${item.itemName} (category: ${category.name}, display: ${category.displayInDownRoomsList})`);
      }

      // Only exclude if displayInDownRoomsList is explicitly set to false
      return shouldInclude;
    });

    console.log('useRoomData - filtered items count:', filteredItems.length);

    const groupedItems = groupItemsByType(filteredItems);

    // Add vendor needed category
    if (!groupedItems[VENDOR_NEEDED_ITEM_TYPE]) {
      groupedItems[VENDOR_NEEDED_ITEM_TYPE] = [];
    }

    VENDORS_LIST.forEach((item) => {
      groupedItems[VENDOR_NEEDED_ITEM_TYPE].push({
        ...item,
        itemType: VENDOR_NEEDED_ITEM_TYPE,
      });
    });

    setItemsByCategory(groupedItems);
    setLoading(false);
  }, [items, itemCategories, groupItemsByType]);

  // Handle room data updates in Firestore
  const handleEdit = useCallback(async (roomId, editType, data) => {
    if (!roomId || !selectedProperty) {
      console.error('HandleEdit called with missing parameters:', { roomId, selectedProperty });
      return;
    }

    const roomRef = propertyDoc(clientId, selectedProperty, 'units', roomId);

    try {
      const audit = updateMeta(user);
      const payload = {
        ...data,
        ...audit,
        // lastUpdatedBy is read by the propertyActionHistory cloud trigger
        lastUpdatedBy: audit.updatedBy,
      };
      // Stamp when the status specifically changed (powers downtime/turn reports)
      if (Object.prototype.hasOwnProperty.call(data, 'status')) {
        payload.statusChangedAt = serverTimestamp();
      }
      await updateDoc(roomRef, payload);
    } catch (error) {
      console.error(`Error updating room ${roomId}:`, error);
      throw error;
    }
  }, [clientId, selectedProperty, user]);

  return {
    itemsByCategory,
    loading,
    handleEdit,
    uniqueCategories: Object.keys(itemsByCategory)
  };
};